import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from '../common/authFetch';
import ConfirmationDialog from '../ConfirmationDialog';
import './BackgroundManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BackgroundManager() {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds`);
      const data = await res.json();
      if (data.success) {
        setImages(data.images);
        return data.images;
      }
    } catch (error) {
      console.error('Failed to fetch backgrounds:', error);
    }
    return [];
  }, []);

  useEffect(() => {
    fetchImages();
    // 서버에서 저장된 프롬프트 로드
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds/default-prompt`);
        const data = await res.json();
        if (data.success) setPrompt(data.prompt);
      } catch (e) { /* ignore */ }
    })();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchImages]);

  // generating 상태 이미지 존재 시 5초 폴링
  useEffect(() => {
    const hasGenerating = images.some(img => img.status === 'generating');

    if (hasGenerating && !pollingRef.current) {
      pollingRef.current = setInterval(async () => {
        const updated = await fetchImages();
        if (!updated.some(img => img.status === 'generating')) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }, 5000);
    } else if (!hasGenerating && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [images, fetchImages]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim() || undefined })
      });
      const data = await res.json();
      if (data.success) {
        fetchImages();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Generate failed:', error);
      toast.error(t('admin.bg.generateFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        fetchImages();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(t('admin.bg.uploadFailed'));
    }
  };

  const handleToggleActive = async (imageId) => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds/${imageId}/active`, {
        method: 'PUT'
      });
      const data = await res.json();
      if (data.success) {
        fetchImages();
      }
    } catch (error) {
      console.error('Toggle failed:', error);
    }
  };

  const handleDelete = (imageId) => {
    setConfirmDialog({
      isOpen: true,
      title: t('confirm'),
      message: t('Are you sure?'),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await authFetch(`${BACKEND_URL}/api/v1/admin/backgrounds/${imageId}`, {
            method: 'DELETE'
          });
          const data = await res.json();
          if (data.success) {
            fetchImages();
          }
        } catch (error) {
          console.error('Delete failed:', error);
        }
      }
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'generating': return t('admin.bg.statusGenerating');
      case 'completed': return t('admin.bg.statusCompleted');
      case 'failed': return t('admin.bg.statusFailed');
      default: return status;
    }
  };

  return (
    <div className="bg-manager">
      <div className="bg-manager-header">
        <h3>{t('admin.bg.title')}</h3>
      </div>

      <div className="bg-prompt-area">
        <label className="bg-prompt-label">{t('admin.bg.promptLabel')}</label>
        <textarea
          className="bg-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          disabled={isGenerating}
        />
        <div className="bg-prompt-hint">{t('admin.bg.promptHint')}</div>
        <div className="bg-btn-group">
          <button
            className="bg-generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? t('admin.bg.generating') : t('admin.bg.generate')}
          </button>
          <button
            className="bg-upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            {t('admin.bg.upload')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {images.length === 0 ? (
        <div className="bg-empty">{t('admin.bg.noImages')}</div>
      ) : (
        <div className="bg-grid">
          {images.map((img) => (
            <div key={img.image_id} className="bg-card">
              <span className={`bg-card-status bg-status-${img.status}`}>
                {getStatusLabel(img.status)}
              </span>

              {img.status === 'completed' && img.file_path ? (
                <img
                  className="bg-card-image"
                  src={`${BACKEND_URL}${img.file_path}`}
                  alt={img.prompt}
                  onClick={() => setPreviewImage(img)}
                />
              ) : (
                <div className="bg-card-placeholder">
                  {img.status === 'generating' ? '...' : (img.error_message || 'Failed')}
                </div>
              )}

              <div className="bg-card-info">
                <div className="bg-card-prompt" title={img.prompt}>{img.prompt}</div>
                <div className="bg-card-actions">
                  {img.status === 'completed' && (
                    <>
                      <button
                        className={`bg-btn-active ${!img.is_active ? 'bg-btn-dimmed' : ''}`}
                        onClick={() => !img.is_active && handleToggleActive(img.image_id)}
                      >
                        {t('admin.bg.active')}
                      </button>
                      <button
                        className={`bg-btn-inactive-sel ${img.is_active ? 'bg-btn-dimmed' : ''}`}
                        onClick={() => img.is_active && handleToggleActive(img.image_id)}
                      >
                        {t('admin.bg.inactive')}
                      </button>
                    </>
                  )}
                  <button
                    className="bg-btn-delete"
                    onClick={() => handleDelete(img.image_id)}
                  >
                    {t('delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewImage && (
        <div className="bg-preview-overlay" onClick={() => setPreviewImage(null)}>
          <div className="bg-preview-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${BACKEND_URL}${previewImage.file_path}`}
              alt={previewImage.prompt}
            />
            <div className="bg-preview-prompt">{previewImage.prompt}</div>
            <button className="bg-preview-close" onClick={() => setPreviewImage(null)}>&times;</button>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmLabel={t('delete')}
        isDanger={true}
      />
    </div>
  );
}

export default BackgroundManager;
